<script setup lang="ts">
import { computed, ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { Check, Download, FileText, MessageCircle, Phone, X } from 'lucide-vue-next'
import type { CustomerRepairDto } from '@ck/api-client'
import { isCustomerVisibleRepair, normalizeReportLifecycle, repairsApi, reportsApi } from '@ck/api-client'
import { formatCurrency } from '@ck/domain'
import { CkAlert, CkButton, CkCard, CkModal, CkTable } from '@ck/ui'
import { runtimeConfig } from '@ck/auth'
import { groupRepairsByStage, repairLineSubtotal, repairTotal, visibleRepairs, type PortalStage } from './repair-approvals'

const props = defineProps<{
  workOrderId: string
  stages?: readonly PortalStage[]
}>()

const queryClient = useQueryClient()
const repairsQuery = useQuery({
  queryKey: ['repairs', props.workOrderId],
  queryFn: () => repairsApi.listByWorkOrder(props.workOrderId)
})

const repairs = computed(() => visibleRepairs((repairsQuery.data.value ?? []).filter(isCustomerVisibleRepair)))
const repairGroups = computed(() => groupRepairsByStage(repairs.value, props.stages ?? []))
const hasRejected = computed(() => repairs.value.some((repair) => repair.status === 'rejected'))
const reportsQuery = useQuery({
  queryKey: ['quote-reports', props.workOrderId],
  queryFn: () => reportsApi.listByWorkOrder(props.workOrderId),
  refetchInterval: 4000
})
const quoteReport = computed(() => (reportsQuery.data.value ?? []).find((report) => String(report.variant).toLowerCase() === 'quote'))
const quoteLifecycle = computed(() => normalizeReportLifecycle(quoteReport.value?.status))
const requestingQuote = ref(false)
const quoteDownloadError = ref(false)
const quoteDownloadPending = ref(false)

async function requestQuoteReport() {
  requestingQuote.value = true
  try {
    await reportsApi.requestQuote(props.workOrderId)
    await reportsQuery.refetch()
  } finally {
    requestingQuote.value = false
  }
}

async function downloadQuoteReport() {
  if (!quoteReport.value) return
  quoteDownloadPending.value = true
  quoteDownloadError.value = false
  try {
    const blob = await reportsApi.download(quoteReport.value.id)
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 30000)
  } catch {
    quoteDownloadError.value = true
  } finally {
    quoteDownloadPending.value = false
  }
}

const quoteColumns = [
  { key: 'description', label: 'Item', width: 'minmax(180px, 1fr)' },
  { key: 'kind', label: 'Tipo', width: '100px' },
  { key: 'quantity', label: 'Qtd.', width: '64px', align: 'right' as const },
  { key: 'unitPrice', label: 'Unitário', width: '112px', align: 'right' as const },
  { key: 'subtotal', label: 'Subtotal', width: '120px', align: 'right' as const }
]

const selectedRepair = ref<CustomerRepairDto | null>(null)
const selectedDecision = ref<'approve' | 'reject' | null>(null)
const rejectNote = ref('')
const decisionMessage = ref('')

const decideMutation = useMutation({
  mutationFn: (vars: { id: string; decision: 'approve' | 'reject'; note?: string | null }) =>
    repairsApi.decide(vars.id, vars.decision, vars.note),
  onSuccess: async (_, vars) => {
    const action = vars.decision === 'approve' ? 'aprovado' : 'recusado'
    decisionMessage.value = `Reparo ${action} com sucesso.`
    closeDecisionModal()
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['repairs', props.workOrderId] }),
      queryClient.invalidateQueries({ queryKey: ['work-order', props.workOrderId] }),
      queryClient.invalidateQueries({ queryKey: ['work-order-detail', props.workOrderId] })
    ])
  }
})

function openDecisionModal(repair: CustomerRepairDto, decision: 'approve' | 'reject') {
  decideMutation.reset()
  decisionMessage.value = ''
  selectedRepair.value = repair
  selectedDecision.value = decision
  rejectNote.value = ''
}

function closeDecisionModal() {
  if (decideMutation.isPending.value) return
  selectedRepair.value = null
  selectedDecision.value = null
  rejectNote.value = ''
}

function confirmDecision() {
  if (!selectedRepair.value || !selectedDecision.value) return
  decideMutation.mutate({
    id: selectedRepair.value.id,
    decision: selectedDecision.value,
    note: selectedDecision.value === 'reject' ? rejectNote.value.trim() || null : null
  })
}

function lineRows(repair: CustomerRepairDto) {
  return repair.quoteLines.map((line) => ({
    ...line,
    kindLabel: line.kind === 'labor' ? 'Mão de obra' : 'Peça',
    subtotalValue: repairLineSubtotal(line)
  }))
}

function groupTotal(group: { repairs: readonly CustomerRepairDto[] }): number {
  return group.repairs.reduce((sum, repair) => sum + repairTotal(repair), 0)
}

function quantityLabel(value: number | string): string {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '—'
}

const whatsappHref = computed(() => {
  if (!runtimeConfig.shopWhatsapp) return null
  const text = encodeURIComponent('Olá! Gostaria de conversar sobre um reparo da minha ordem de serviço.')
  return `https://wa.me/${runtimeConfig.shopWhatsapp}?text=${text}`
})
const phoneHref = computed(() => (runtimeConfig.shopPhone ? `tel:${runtimeConfig.shopPhone}` : null))
</script>

<template>
  <CkCard v-if="repairsQuery.isPending.value || repairsQuery.isError.value || repairs.length" class="repairs">
    <div class="section-heading">
      <div>
        <div class="ck-eyebrow">Orçamento por etapa</div>
        <h2>Reparos e orçamentos</h2>
      </div>
      <span v-if="repairs.length" class="quote-count">{{ repairs.length }} {{ repairs.length === 1 ? 'reparo' : 'reparos' }}</span>
      <div class="quote-report-actions">
        <CkButton v-if="!quoteReport || quoteLifecycle === 'failed'" variant="ghost" :disabled="requestingQuote" @click="requestQuoteReport"><FileText :size="15" aria-hidden="true" />{{ requestingQuote ? 'Solicitando…' : 'Gerar relatório do orçamento' }}</CkButton>
        <span v-else-if="quoteLifecycle === 'queued'" class="report-status"><span class="status-dot queued" aria-hidden="true" />Gerando relatório…</span>
        <CkButton v-else variant="secondary" :disabled="quoteDownloadPending" @click="downloadQuoteReport"><Download :size="15" aria-hidden="true" />{{ quoteDownloadPending ? 'Abrindo…' : 'Baixar orçamento em PDF' }}</CkButton>
      </div>
    </div>
    <CkAlert v-if="quoteDownloadError" tone="error">Não foi possível baixar o PDF do orçamento. Tente novamente.</CkAlert>

    <p v-if="repairsQuery.isPending.value" class="empty-inline">Carregando orçamento…</p>
    <CkAlert v-else-if="repairsQuery.isError.value" tone="error">Não foi possível carregar os reparos desta ordem de serviço. Tente novamente.</CkAlert>
    <p v-else-if="repairs.length === 0" class="empty-inline">Nenhum orçamento publicado para esta ordem de serviço.</p>

    <CkAlert v-if="decisionMessage" tone="success" aria-live="polite">{{ decisionMessage }}</CkAlert>
    <CkAlert v-if="decideMutation.isError.value" tone="error" aria-live="assertive">Não foi possível registrar sua decisão. Feche a mensagem e tente novamente.</CkAlert>

    <div v-for="group in repairGroups" :key="group.key" class="stage-block">
      <div class="stage-heading">
        <div>
          <div class="block-title">Etapa</div>
          <h3>{{ group.title }}</h3>
        </div>
        <strong class="stage-total">{{ formatCurrency(groupTotal(group)) }}</strong>
      </div>

      <article v-for="repair in group.repairs" :key="repair.id" class="repair" :class="repair.status">
        <div class="repair-head">
          <div>
            <strong>{{ repair.title }}</strong>
            <span v-if="repair.status === 'pendingApproval'" class="badge pending">Aguardando aprovação</span>
            <span v-else-if="repair.status === 'approved'" class="badge approved">Aprovado</span>
            <span v-else-if="repair.status === 'rejected'" class="badge rejected">Recusado</span>
          </div>
          <strong class="repair-total">{{ formatCurrency(repairTotal(repair)) }}</strong>
        </div>
        <p v-if="repair.description" class="desc">{{ repair.description }}</p>

        <div class="lines-label">Itens do orçamento</div>
        <CkTable
          class="quote-table"
          :columns="quoteColumns"
          :rows="lineRows(repair)"
          :row-key="(row) => row.id"
          empty-message="Nenhum item informado."
        >
          <template #default="{ item }">
            <span>{{ item.description }}</span>
            <span>{{ item.kindLabel }}</span>
            <span class="number">{{ quantityLabel(item.quantity) }}</span>
            <span class="number">{{ formatCurrency(item.unitPrice) }}</span>
            <span class="number">{{ formatCurrency(item.subtotalValue) }}</span>
          </template>
          <template #footer>
            <span class="footer-total-label">Total do reparo</span>
            <strong class="number">{{ formatCurrency(repairTotal(repair)) }}</strong>
          </template>
        </CkTable>

        <div v-if="repair.status === 'pendingApproval'" class="actions">
          <CkButton variant="ghost" class="reject" :disabled="decideMutation.isPending.value" @click="openDecisionModal(repair, 'reject')"><X :size="16" aria-hidden="true"/>Recusar</CkButton>
          <CkButton :disabled="decideMutation.isPending.value" @click="openDecisionModal(repair, 'approve')"><Check :size="16" aria-hidden="true"/>Aprovar</CkButton>
        </div>
        <p v-if="repair.status === 'rejected' && repair.customerDecisionNote" class="decision-note">Seu motivo: {{ repair.customerDecisionNote }}</p>
      </article>
    </div>

    <div v-if="hasRejected && (whatsappHref || phoneHref)" class="contact">
      <p>Recusou um reparo? Fale com a oficina para alinhar os próximos passos.</p>
      <div class="contact-actions">
        <a v-if="whatsappHref" :href="whatsappHref" target="_blank" rel="noopener" class="wa"><MessageCircle :size="16" aria-hidden="true"/>WhatsApp</a>
        <a v-if="phoneHref" :href="phoneHref" class="tel"><Phone :size="16" aria-hidden="true"/>Ligar</a>
      </div>
    </div>
  </CkCard>

  <CkModal
    v-if="selectedRepair && selectedDecision"
    :title="selectedDecision === 'approve' ? 'Confirmar aprovação' : 'Confirmar recusa'"
    @close="closeDecisionModal"
  >
    <p class="modal-copy">
      {{ selectedDecision === 'approve'
        ? `Você confirma a aprovação do reparo “${selectedRepair.title}” no valor de ${formatCurrency(repairTotal(selectedRepair))}?`
        : `Você confirma a recusa do reparo “${selectedRepair.title}” no valor de ${formatCurrency(repairTotal(selectedRepair))}?` }}
    </p>
    <label v-if="selectedDecision === 'reject'" class="note-field" for="reject-note">
      <span>Motivo da recusa (opcional)</span>
      <textarea id="reject-note" v-model="rejectNote" rows="3" :disabled="decideMutation.isPending.value" maxlength="500" />
    </label>
    <CkAlert v-if="decideMutation.isError.value" tone="error">Não foi possível registrar sua decisão. Verifique sua conexão e tente novamente.</CkAlert>
    <div class="modal-actions">
      <CkButton variant="ghost" :disabled="decideMutation.isPending.value" @click="closeDecisionModal">Voltar</CkButton>
      <CkButton
        :variant="selectedDecision === 'reject' ? 'secondary' : 'primary'"
        :class="{ 'confirm-reject': selectedDecision === 'reject' }"
        :disabled="decideMutation.isPending.value"
        @click="confirmDecision"
      >
        {{ decideMutation.isPending.value ? 'Enviando…' : selectedDecision === 'approve' ? 'Confirmar aprovação' : 'Confirmar recusa' }}
      </CkButton>
    </div>
  </CkModal>
</template>

<style scoped>
.repairs { margin-top: 24px; }
.section-heading, .stage-heading, .repair-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); }
h2 { margin: var(--space-1) 0 0; font-size: var(--fs-xl); }
h3 { margin: var(--space-1) 0 0; font-size: var(--fs-lg); }
.quote-count { flex: 0 0 auto; padding: 5px 10px; border-radius: var(--radius-full); background: var(--ck-cream-200); color: var(--ck-maroon-700); font-size: var(--fs-xs); font-weight: var(--fw-bold); }
.quote-report-actions { display: flex; align-items: center; gap: var(--space-2); margin-left: auto; }
.quote-report-actions :deep(.ck-button) { display: inline-flex; align-items: center; gap: var(--space-2); }
.report-status { display: inline-flex; align-items: center; gap: var(--space-2); color: var(--ck-text-muted); font-size: var(--fs-xs); }
.status-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--ck-orange-500); }
.empty-inline { color: var(--ck-text-muted); font-size: var(--fs-sm); }
.stage-block { margin-top: var(--space-6); }
.stage-heading { align-items: center; margin-bottom: var(--space-3); padding-bottom: var(--space-2); border-bottom: 1px solid var(--ck-cream-300); }
.block-title, .lines-label { color: var(--ck-maroon-700); font-size: var(--fs-2xs); font-weight: var(--fw-bold); letter-spacing: var(--ls-widest); text-transform: uppercase; }
.stage-total, .repair-total, .number { font-family: var(--font-mono); font-variant-numeric: tabular-nums; }
.stage-total { color: var(--ck-maroon-700); font-size: var(--fs-sm); }
.repair { margin-bottom: var(--space-4); padding: var(--space-4); border: 1px solid var(--ck-cream-300); border-left: 4px solid var(--ck-cream-400); border-radius: var(--radius-lg); background: var(--ck-cream-50); }
.repair.pendingApproval { border-left-color: var(--ck-orange-500); }
.repair.approved { border-left-color: var(--ck-ok-500); }
.repair.rejected { border-left-color: var(--ck-fail-500); }
.repair-head strong { display: block; }
.repair-total { flex: 0 0 auto; color: var(--ck-text); }
.badge { display: inline-flex; margin: var(--space-2) 0 0; padding: 3px 8px; border-radius: var(--radius-full); font-size: var(--fs-2xs); font-weight: var(--fw-bold); }
.badge.pending { background: var(--ck-warn-bg); color: var(--ck-warn-text); }
.badge.approved { background: var(--ck-ok-bg); color: var(--ck-ok-text); }
.badge.rejected { background: var(--ck-fail-bg); color: var(--ck-fail-text); }
.desc, .decision-note { margin: var(--space-3) 0 0; color: var(--ck-text-muted); font-size: var(--fs-sm); line-height: var(--lh-normal); }
.decision-note { padding: var(--space-3); border-radius: var(--radius-md); background: var(--ck-fail-bg); color: var(--ck-fail-text); }
.lines-label { margin: var(--space-4) 0 var(--space-2); letter-spacing: var(--ls-wide); }
.quote-table { overflow-x: auto; }
.quote-table :deep(.ck-table-row) { min-width: 640px; }
.quote-table :deep(.ck-table-foot) { align-items: center; }
.footer-total-label { margin-right: auto; color: var(--ck-text-muted); font-size: var(--fs-sm); font-weight: var(--fw-semibold); }
.actions, .modal-actions, .contact-actions { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-2); flex-wrap: wrap; }
.actions { margin-top: var(--space-3); }
.actions :deep(.ck-button), .modal-actions :deep(.ck-button) { display: inline-flex; align-items: center; gap: var(--space-2); }
.reject { color: var(--ck-fail-text); }
.confirm-reject { color: var(--ck-fail-text); border-color: var(--ck-fail-500); background: var(--ck-fail-bg); }
.contact { margin-top: var(--space-5); padding: var(--space-4); border-radius: var(--radius-md); background: var(--ck-info-bg); color: var(--ck-info-text); }
.contact p { margin: 0 0 var(--space-3); font-size: var(--fs-sm); }
.contact-actions { justify-content: flex-start; }
.contact-actions a { display: inline-flex; align-items: center; gap: var(--space-2); min-height: 44px; padding: 9px 14px; border-radius: var(--radius-md); font-size: var(--fs-sm); font-weight: var(--fw-semibold); text-decoration: none; }
.wa { background: var(--ck-ok-bg); color: var(--ck-ok-text); border: 1px solid var(--ck-ok-500); }
.tel { background: var(--ck-cream-50); color: var(--ck-maroon-700); border: 1px solid var(--ck-cream-300); }
.modal-copy { margin: 0 0 var(--space-5); line-height: var(--lh-relaxed); }
.note-field { display: block; margin-bottom: var(--space-4); color: var(--ck-text-muted); font-size: var(--fs-sm); font-weight: var(--fw-semibold); }
.note-field span { display: block; margin-bottom: var(--space-2); }
.note-field textarea { width: 100%; min-height: 92px; padding: 10px 12px; border: 1px solid var(--ck-cream-300); border-radius: var(--radius-md); background: var(--ck-cream-50); color: var(--ck-text); font: inherit; resize: vertical; }
.note-field textarea:focus-visible { outline: 3px solid color-mix(in srgb, var(--ck-orange-500) 45%, transparent); outline-offset: 2px; border-color: var(--ck-orange-500); }
.modal-actions { margin-top: var(--space-5); }
@media (max-width: 620px) {
  .section-heading, .stage-heading, .repair-head { flex-direction: column; }
  .quote-report-actions { width: 100%; margin-left: 0; }
  .quote-report-actions :deep(.ck-button) { width: 100%; }
  .repair-head { gap: var(--space-2); }
  .repair-total { align-self: flex-start; }
  .stage-total { align-self: flex-start; }
  .modal-actions { justify-content: stretch; }
  .modal-actions :deep(.ck-button) { flex: 1; }
}
</style>
